import { ProjectNavbarId } from "@/components/navigation/ProjectNavbarCommon.constants";
import { TeamNavbarPageId } from "@/components/navigation/TeamNavbar.constants";
import { useSqlWorksheetCount } from "@/components/navigation/useSqlWorksheetCount";
import { useCategoryNavEnabled } from "@/utils/hooks/use-category-nav-enabled";
import {
  getProjectNavbarCounts,
  getTeamNavbarCounts,
} from "@/gen_src/proto/chalk/server/v1/inventory-InventoryService_connectquery";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { formatCompactCount } from "@/utils/string-utils";
import { useQuery } from "@connectrpc/connect-query";
import { useMemo } from "react";

// Format a non-negative int64 count for a badge. Zero/unset render no badge.
const toBadgeCount = (value: bigint | undefined): string | undefined => {
  if (value == null || value === BigInt(0)) return undefined;
  return formatCompactCount(Number(value));
};

// Same, for a count the list it badges does not agree with: the suffix marks
// the number as a floor rather than a total. Zero still renders no badge, so
// the suffix never lands on its own.
const toAtLeastBadgeCount = (value: bigint | undefined): string | undefined => {
  const count = toBadgeCount(value);
  return count == null ? undefined : `${count}+`;
};

// Project navbar + Settings sub-navbar counts, keyed by the navbar id they
// badge: a subpage id, or a page id for a section row with no visible subpages.
// Environment comes from the request context, so the request is empty.
export const useProjectNavbarCounts = (): Partial<Record<ProjectNavbarId, string>> => {
  const { environmentId } = useRouteAndTeamInfo();
  const { data: counts } = useQuery(getProjectNavbarCounts, {}, { enabled: environmentId != null });
  // SQL is not in the inventory RPC; it counts the worksheets the SQL explorer lists. Only the
  // category nav badges the SQL row, so only it pays for the worksheet requests.
  const categoryNavEnabled = useCategoryNavEnabled();
  const sqlWorksheetCount = useSqlWorksheetCount({ enabled: categoryNavEnabled });

  return useMemo(() => {
    const sqlBadge =
      sqlWorksheetCount == null ? undefined : toBadgeCount(BigInt(sqlWorksheetCount));
    if (counts == null) return sqlBadge == null ? {} : { "explorer-sql": sqlBadge };

    const byId: Partial<Record<ProjectNavbarId, string | undefined>> = {
      "explorer-sql": sqlBadge,
      // Project navbar. Resolvers and features are scoped server-side to the
      // environment's active deployment.
      resolvers: toBadgeCount(counts.resolvers),
      features: toBadgeCount(counts.features),
      "named-queries": toBadgeCount(counts.namedQueries),
      schedules: toBadgeCount(counts.activeScheduledQueries),
      "scaling-groups": toBadgeCount(counts.scalingGroups),
      // Counts unstopped containers, while the Sandboxes list shows every
      // status, so the badge is a lower bound on what the page lists.
      sandboxes: toAtLeastBadgeCount(counts.sandboxes),
      // Badges the Notebooks section row itself, which links straight to the
      // notebooks list and has no subpages.
      "notebooks-nav": toBadgeCount(counts.notebooks),
      "external-functions": toBadgeCount(counts.functions),
      "container-snapshots": toBadgeCount(counts.snapshots),
      images: toBadgeCount(counts.images),
      // Volumes intentionally have no badge: they are live cluster state
      // behind a per-environment proxy, so counting them would put a cluster
      // round-trip on every navbar load.
      "model-registry": toBadgeCount(counts.models),
      "model-deployments": toBadgeCount(counts.modelDeployments),
      incidents: toBadgeCount(counts.openIncidents),
      webhooks: toBadgeCount(counts.webhooks),
      data_sources: toBadgeCount(counts.integrations),
      "online-store": toBadgeCount(counts.onlineStores),
      "offline-store": toBadgeCount(counts.offlineStoreConnections),
      "vector-db": toBadgeCount(counts.vectorDatabases),
      secrets: toBadgeCount(counts.secrets),
      "environment-variables": toBadgeCount(counts.configVariables),
      // Settings sub-navbar.
      tokens: toBadgeCount(counts.accessTokens),
      "ai-connections": toBadgeCount(counts.aiConnections),
      buckets: toBadgeCount(counts.buckets),
    };

    return byId;
  }, [counts, sqlWorksheetCount]);
};

// Team navbar counts, keyed by the page id they badge. Team comes from the
// request context.
export const useTeamNavbarCounts = (): Partial<Record<TeamNavbarPageId, string>> => {
  const { data: counts } = useQuery(getTeamNavbarCounts, {});

  return useMemo(() => {
    if (counts == null) return {};

    const byId: Partial<Record<TeamNavbarPageId, string | undefined>> = {
      projects: toBadgeCount(counts.projects),
      "cloud-accounts": toBadgeCount(counts.accounts),
      "cloud-clusters": toBadgeCount(counts.clusters),
      "cloud-container-registries": toBadgeCount(counts.registries),
      "cloud-vpcs": toBadgeCount(counts.vpcs),
      "cloud-storage": toBadgeCount(counts.storage),
    };

    return byId;
  }, [counts]);
};
